import { Button, ButtonProps, CircularProgress } from "@mui/material";
import { styled } from "@mui/material/styles";
import { alpha, darken } from "@mui/system"; // ✅ MUI theme-compatible styled
import React from "react";

interface CustomButtonProps extends ButtonProps {
  loading?: boolean;
  baseColor?: string;
}

const CustomButton: React.FC<CustomButtonProps> = ({
  loading,
  startIcon,
  disabled,
  children,
  baseColor,
  variant = "contained",
  color,
  ...rest
}) => {
  const isUsingMUIColor = !!color;

  // Fallback baseColor logic
  const finalBaseColor = isUsingMUIColor ? undefined : baseColor || "#541C4C";

  const hoverColor = finalBaseColor ? darken(finalBaseColor, 0.2) : undefined;
  const borderColor = finalBaseColor ?? "";
  const textColor = variant === "contained" ? "#fff" : finalBaseColor ?? "";

  const backgroundColor =
    variant === "contained" ? finalBaseColor ?? undefined : variant === "outlined" ? "transparent" : "transparent";

  const hoverBackgroundColor =
    variant === "contained" ? hoverColor ?? undefined : finalBaseColor ? alpha(finalBaseColor, 0.08) : undefined;

  const StyledSpinner = styled(CircularProgress)({
    color: isUsingMUIColor ? undefined : textColor,
  });

  const StyledButton = styled(Button)(() => ({
    ...(isUsingMUIColor
      ? {} // Let MUI handle it
      : {
          textTransform: "none",
          color: textColor,
          backgroundColor,
          border: variant === "outlined" ? `1px solid ${borderColor}` : "none",
          "&:hover": {
            backgroundColor: hoverBackgroundColor,
            border: variant === "outlined" ? `1px solid ${darken(borderColor, 0.3)}` : undefined,
          },
          "&.Mui-disabled": {
            backgroundColor: "#e0e0e0",
            color: "#9e9e9e",
            border: variant === "outlined" ? "1px solid #c0c0c0" : "none",
          },
        }),
  }));

  return (
    <StyledButton
      variant={variant}
      color={isUsingMUIColor ? color : undefined}
      disabled={disabled || loading}
      startIcon={loading ? <StyledSpinner size={20} /> : startIcon}
      {...rest}
    >
      {children}
    </StyledButton>
  );
};

export default CustomButton;
